<template>
  <div class="container">
    <div class="title-container">
      <h1 class="graph-title">Score Distribution</h1>
      <div class="toggle">
        <InfinityToggle :value="infiniteMode" @handleToggle="toggleInfiniteMode" />
      </div>
    </div>
    <div class="graph-container">
      <canvas ref="scoreGraph"></canvas>
    </div>
  </div>
</template>

<script>
  import Chart from 'chart.js/auto';
  import InfinityToggle from './InfinityToggle.vue';

  export default {
    name: 'ScoreDistributionGraph',
    props: ['scoreDistribution', 'infinityScoreDistribution'],
    components: {
      InfinityToggle,
    },
    data() {
      return {
        infiniteMode: false,
      };
    },
    mounted() {
      // default to infinite mode if it is the only distribution with data
      if (
        Object.values(this.scoreDistribution).every((v) => v === 0) &&
        Object.values(this.infinityScoreDistribution).some((v) => v > 0)
      ) {
        this.infiniteMode = true;
      }

      this.$nextTick(() => {
        this.initScoreGraph();
      });
    },
    methods: {
      toggleInfiniteMode() {
        this.infiniteMode = !this.infiniteMode;
        this.updateChartData();
      },
      updateChartData() {
        if (!this.scoreGraph) return;

        const chart = this.scoreGraph;
        const dataset = chart.data.datasets[0];

        dataset.data = this.infiniteMode ? this.infinityScoreDistribution : this.scoreDistribution;

        chart.update();
      },
      initScoreGraph() {
        const ctx = this.$refs.scoreGraph.getContext('2d');

        // Destroy existing chart if it exists
        if (this.scoreGraph) {
          this.scoreGraph.destroy();
        }

        const isMobile = window.innerWidth <= 768;

        this.scoreGraph = new Chart(ctx, {
          type: 'bar',
          data: {
            datasets: [
              {
                data: this.infiniteMode ? this.infinityScoreDistribution : this.scoreDistribution,
                backgroundColor: 'rgba(147, 147, 236, 1)',
                hoverBackgroundColor: 'rgba(147, 147, 236, 1)',
                borderRadius: 8,
                borderSkipped: 'bottom',
                barThickness: isMobile ? 20 : 24,
              },
            ],
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: {
                display: false,
              },
              tooltip: {
                enabled: false,
                external: this.externalTooltipHandler,
              },
            },
            onHover: (event, activeElements, chart) => {
              const dataset = chart.data.datasets[0];
              const baseColor = 'rgba(147, 147, 236, 1)';
              const dimColor = 'rgba(69, 69, 140, 1)';
              const count = chart.getDatasetMeta(0).data.length;

              // Nothing hovered
              if (!activeElements.length) {
                dataset.backgroundColor = Array(count).fill(baseColor);
                chart.update('none');
                return;
              }

              const hoveredIndex = activeElements[0].index;

              dataset.backgroundColor = Array.from({ length: count }, (_, i) =>
                i === hoveredIndex ? baseColor : dimColor
              );

              chart.update('none');
            },
            onLeave: (event, chart) => {
              const dataset = chart.data.datasets[0];
              const baseColor = 'rgba(147, 147, 236, 1)';
              dataset.backgroundColor = Array(dataset.data.length).fill(baseColor);
              chart.update('none');
            },
            scales: {
              y: {
                title: {
                  display: true,
                  text: '# of Games',
                  color: 'white',
                  font: {
                    weight: 700,
                    size: 12,
                  },
                },
                grid: {
                  color: 'rgba(76, 76, 103, 1)',
                },
                border: {
                  display: false,
                },
                beginAtZero: true,
                ticks: {
                  color: 'white',
                  padding: 8,
                  stepSize: 1,
                  precision: 0,
                  callback: (value) => (Number.isInteger(value) ? value : ''),
                },
              },
              x: {
                grid: {
                  display: false,
                },
                border: {
                  display: false,
                },
                ticks: {
                  color: 'white',
                },
              },
            },
          },
        });
      },
      getOrCreateTooltip(chart) {
        let tooltipEl = chart.canvas.parentNode.querySelector('div');

        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.style.background = 'white';
          tooltipEl.style.borderRadius = '8px';
          tooltipEl.style.color = 'rgba(53, 53, 72, 1)';
          tooltipEl.style.opacity = 1;
          tooltipEl.style.padding = '4px 12px';
          tooltipEl.style.pointerEvents = 'none';
          tooltipEl.style.position = 'absolute';
          tooltipEl.style.transform = 'translate(-50%, -120%)';
          tooltipEl.style.transition = 'all .3s ease';

          const table = document.createElement('table');
          table.style.margin = '0';
          table.style.borderCollapse = 'collapse';
          table.style.borderSpacing = '0';

          tooltipEl.appendChild(table);
          chart.canvas.parentNode.appendChild(tooltipEl);
        }

        return tooltipEl;
      },
      externalTooltipHandler(context) {
        // Tooltip Element
        const { chart, tooltip } = context;
        const tooltipEl = this.getOrCreateTooltip(chart);

        // Hide if no tooltip
        if (tooltip.opacity === 0) {
          tooltipEl.style.opacity = 0;
          return;
        }

        // Set Text
        if (tooltip.body) {
          const title = (tooltip.title && tooltip.title[0]) || '';
          const dataSource = this.infiniteMode
            ? this.infinityScoreDistribution
            : this.scoreDistribution;
          const numGames = (dataSource && dataSource[title]) ?? 0;

          const tableRoot = tooltipEl.querySelector('table');

          // Clear previous content
          while (tableRoot.firstChild) {
            tableRoot.firstChild.remove();
          }

          // First row: number of games
          const trTitle = document.createElement('tr');
          const th = document.createElement('th');
          th.style.fontSize = '14px';
          th.style.fontWeight = '700';
          th.style.lineHeight = '1.1';
          th.style.padding = '0';
          th.style.margin = '0';
          th.style.border = '0';
          th.textContent = numGames;

          trTitle.appendChild(th);

          // Second row: label "games"
          const trBody = document.createElement('tr');
          const td = document.createElement('td');
          td.style.fontSize = '10px';
          td.style.padding = '0';
          td.style.margin = '0';
          td.style.border = '0';
          td.textContent = numGames === 1 ? 'game' : 'games';

          trBody.appendChild(td);

          tableRoot.appendChild(trTitle);
          tableRoot.appendChild(trBody);
        }

        const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

        // Display, position, and set styles for font
        tooltipEl.style.opacity = 1;
        tooltipEl.style.left = positionX + tooltip.caretX + 'px';
        tooltipEl.style.top = positionY + tooltip.caretY + 'px';
      },
    },
  };
</script>

<style scoped>
  .container {
    width: 95svw;
    max-width: 650px;
    display: flex;
    flex-direction: column;
    border-radius: 16px;
    gap: 24px;
    padding: 16px 32px 16px 16px;
    background-color: rgba(42, 42, 58, 1);
    color: white;
    box-sizing: border-box;
  }

  .title-container {
    position: relative;
  }

  .graph-title {
    text-align: center;
    font-weight: 700;
    font-size: 16px;
    margin: 0;
  }

  .toggle {
    position: absolute;
    right: 0;
    top: 0;
  }

  .graph-container {
    display: flex;
    width: 100%;
    min-height: 300px;
  }

  @media screen and (max-width: 768px) {
    .graph-container {
      min-height: 200px;
    }
  }
</style>
